import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useTranslation } from '../i18n';
import { analytics } from '../hooks/useAnalytics';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import { STALE_TIMES } from '../constants/staleTimes';

// ─── types ────────────────────────────────────────────────────────────────────

interface Level {
  id: string;
  name: string;
  code: string;
  sort_order: number;
}

interface DisabledSpot {
  id: string;
  spot_number: string;
  level_label: string;
  level_code: string;
  description: string | null;
}

// ─── RegisterSpotPage ─────────────────────────────────────────────────────────

export default function RegisterSpotPage() {
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const { t }    = useTranslation();

  const [levelId,     setLevelId]     = useState('');
  const [spotNumber,  setSpotNumber]  = useState('');
  const [description, setDescription] = useState('');
  const [apiError,    setApiError]    = useState<string | null>(null);

  const { data: levels, isLoading: levelsLoading } = useQuery({
    queryKey: ['levels'],
    queryFn:  () => apiClient.get<{ data: Level[] }>('/buildings/levels').then((r) => r.data.data),
    staleTime: STALE_TIMES.STATIC,
  });

  const { data: disabledSpots } = useQuery({
    queryKey: ['spots', 'mine', 'disabled'],
    queryFn:  () => apiClient.get<{ data: DisabledSpot[] }>('/spots/mine/disabled').then((r) => r.data.data),
    staleTime: STALE_TIMES.SLOW,
  });

  const isValid = levelId !== '' && spotNumber.trim() !== '';

  const registerMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post<{ data: { id: string } }>('/spots', {
          level_id:    levelId,
          spot_number: spotNumber.trim(),
          description: description.trim() || undefined,
        })
        .then((r) => r.data.data),
    onSuccess: (spot) => {
      const levelCode = levels?.find((l) => l.id === levelId)?.code || '';
      analytics.trackSpotRegistered(levelCode);
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine', 'disabled'] });
      navigate(`/spots/${spot.id}`);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'SPOT_LIMIT_REACHED') setApiError('You have reached the maximum of 4 spots');
      else if (code === 'SPOT_ALREADY_EXISTS') setApiError('A spot with this number already exists on that level');
      else setApiError('Something went wrong. Please try again.');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (spotId: string) =>
      apiClient
        .post<{ data: { id: string } }>(`/spots/${spotId}/restore`)
        .then((r) => r.data.data),
    onSuccess: (spot) => {
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine', 'disabled'] });
      navigate(`/spots/${spot.id}`);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'SPOT_LIMIT_REACHED') setApiError('You have reached the maximum of 4 spots');
      else setApiError('Could not restore spot. Please try again.');
    },
  });

  const isMutating = registerMutation.isPending || restoreMutation.isPending;

  return (
    <div style={page}>
      {/* Top bar */}
      <header className="topbar" style={{
        position:       'sticky',
        top:            0,
        zIndex:         50,
        height:         '64px',
        padding:        '0 24px',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        boxShadow:      '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <button onClick={() => navigate(-1)} style={backBtn} aria-label="Go back">
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '24px', color: '#005bc0' }} />
        </button>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      <div style={body}>

        {/* Previously removed spots */}
        {(disabledSpots ?? []).length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <div style={sectionLabel}>{t('previously_removed')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(disabledSpots ?? []).map((spot) => (
                <div
                  key={spot.id}
                  style={{
                    display:         'flex',
                    alignItems:      'center',
                    justifyContent:  'space-between',
                    padding:         '12px 14px',
                    background:      '#ffffff',
                    borderRadius:    '12px',
                    boxShadow:       '0 2px 8px rgba(43,52,55,0.06)',
                    border:          '1px solid rgba(43,52,55,0.06)',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--on-surface)' }}>
                      {spot.level_label} · {spot.spot_number}
                    </div>
                    {spot.description && (
                      <div style={{ fontSize: '12px', color: 'var(--on-surface-3)', marginTop: '2px' }}>
                        {spot.description}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setApiError(null); restoreMutation.mutate(spot.id); }}
                    disabled={isMutating}
                    style={{
                      height:       '32px',
                      padding:      '0 14px',
                      borderRadius: '8px',
                      border:       'none',
                      background:   'var(--primary-container)',
                      color:        'var(--primary)',
                      fontSize:     '13px',
                      fontWeight:   600,
                      cursor:       isMutating ? 'not-allowed' : 'pointer',
                      flexShrink:   0,
                      marginLeft:   '12px',
                    }}
                  >
                    {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e3e9ec' }} />
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--on-surface-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('or_register_new')}
              </span>
              <div style={{ flex: 1, height: '1px', background: '#e3e9ec' }} />
            </div>
          </div>
        )}

        {/* Level select */}
        <div style={{ marginBottom: '20px' }}>
          <label style={sectionLabel}>{t('parking_level')}</label>
          {levelsLoading ? (
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>{t('loading_levels')}</div>
          ) : (
            <select
              value={levelId}
              onChange={(e) => setLevelId(e.target.value)}
              style={selectStyle}
            >
              <option value="">{t('select_level')}</option>
              {(levels ?? []).map((level) => (
                <option key={level.id} value={level.id}>
                  {t(level.code) || level.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Spot number */}
        <div style={{ marginBottom: '20px' }}>
          <label style={sectionLabel}>{t('spot_number')}</label>
          <input
            type="text"
            value={spotNumber}
            onChange={(e) => setSpotNumber(e.target.value)}
            placeholder={t('spot_number_placeholder')}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '20px' }}>
          <label style={sectionLabel}>{t('description_optional')}</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('description_placeholder')}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Info card */}
        <div style={{ marginBottom: '24px', padding: '20px', borderRadius: '20px', backgroundColor: '#ffffff', boxShadow: '0 4px 16px rgba(43,52,55,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#d8e2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" fill="#005bc0">
                <path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/>
              </svg>
            </div>
            <span style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 700, fontSize: '16px', color: '#2b3437' }}>{t('what_happens_next')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {[
              { label: t('registration'), desc: t('registration_desc') },
              { label: t('actions'),      desc: t('actions_desc') },
              { label: t('management'),  desc: t('management_desc') },
            ].map((step, i, arr) => (
              <div key={step.label} style={{ display: 'flex', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#005bc0', flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ width: '1px', flexGrow: 1, backgroundColor: '#e3e9ec', margin: '4px 0' }} />
                  )}
                </div>
                <div style={{ paddingBottom: i < arr.length - 1 ? '14px' : 0 }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: '#2b3437', margin: 0 }}>{step.label}</p>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '2px 0 0' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {apiError && (
          <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '13px' }}>
            {apiError}
          </div>
        )}

        <button
          onClick={() => { setApiError(null); registerMutation.mutate(); }}
          disabled={!isValid || isMutating}
          style={{ ...btn, opacity: (!isValid || isMutating) ? 0.5 : 1 }}
        >
          {registerMutation.isPending ? t('registering') : t('register_spot')}
        </button>
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', marginTop: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
          {t('register_agreement')}
        </p>
      </div>
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', minHeight: '100dvh',
  maxWidth: '430px', margin: '0 auto', backgroundColor: '#f8f9fa',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
};


const backBtn: React.CSSProperties = {
  width:          '40px',
  height:         '40px',
  border:         'none',
  background:     'none',
  cursor:         'pointer',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  borderRadius:   '9999px',
  flexShrink:     0,
};

const body: React.CSSProperties = { flex: 1, padding: '16px', overflowY: 'auto' };

const sectionLabel: React.CSSProperties = {
  display: 'block', fontSize: '13px', fontWeight: 600,
  color: '#374151', marginBottom: '8px',
};

const selectStyle: React.CSSProperties = {
  width: '100%', height: '40px', borderRadius: '8px',
  border: '1px solid rgba(43,52,55,0.12)', padding: '0 10px',
  fontSize: '14px', backgroundColor: '#ffffff',
  appearance: 'auto', boxSizing: 'border-box',
};

const inputStyle: React.CSSProperties = {
  flex: 1, height: '40px', borderRadius: '8px',
  border: '1px solid rgba(43,52,55,0.12)', padding: '0 10px',
  fontSize: '14px', backgroundColor: '#ffffff',
};

const btn: React.CSSProperties = {
  display: 'block', width: '100%', height: '44px', borderRadius: '8px',
  border: 'none', background: 'linear-gradient(135deg, #005bc0, #004fa9)',
  color: '#f7f7ff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
};
